import type { Transaction } from "@/apis/gql/types";
import { SmbAttachmentStatus } from "@/apis/gql/types";
import {
  TableActionBarButton,
  TableActionBarRoot,
  TableActionBarRowsSelected,
} from "@/components/shared/data-table/action-bar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDeleteBankTransaction } from "@/queries/transactions/delete-transaction";
import { useUpdateBankTransaction } from "@/queries/transactions/update-bank-transaction";
import type { Table as TableType } from "@tanstack/react-table";
import {
  CircleOff,
  EllipsisVertical,
  HelpCircle,
  Link2,
  Trash2,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";

type Props = {
  table: TableType<Transaction>;
  handleMatch: () => Promise<void>;
  selectedInvoicesIds: Record<string, boolean>;
};

export const TransactionsActionBar = ({
  table,
  handleMatch,
  selectedInvoicesIds,
}: Props) => {
  const { t } = useTranslation();
  const [deleteBankTransaction, { loading }] = useDeleteBankTransaction();
  const [updateBankTransaction, { loading: updateLoading }] =
    useUpdateBankTransaction();

  const selectedRows = table.getSelectedRowModel().rows;

  if (selectedRows.length === 0) return null;

  const showChangeStatus = selectedRows.every(
    (row) =>
      ![
        SmbAttachmentStatus.ATTACHED,
        SmbAttachmentStatus.ATTACHMENT_SUGGESTED,
      ].includes(row.original.smbAttachmentStatus),
  );

  return (
    <TableActionBarRoot>
      <TableActionBarRowsSelected table={table} />
      <div className="ml-auto flex items-center gap-2">
        {showChangeStatus && (
          <Select
            onValueChange={handleChangeStatus}
            disabled={updateLoading}
            value="default"
          >
            <SelectTrigger className="w-[220px] h-10 hover:bg-gray-100">
              <span className="text-sm font-medium">
                {t("transactions.actions.change_status")}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                value={SmbAttachmentStatus.NOT_ATTACHED}
                hideIndicator
                className="pl-2 cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <HelpCircle className="mt-1 h-4 w-4 text-orange-600" />
                  <div className="flex flex-col text-left">
                    <span className="text-sm font-medium">
                      {t("transactions.transaction_smb_status.not_attached")}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t("transactions.status_options.missing_description")}
                    </span>
                  </div>
                </div>
              </SelectItem>
              <SelectItem
                value={SmbAttachmentStatus.NOT_REQUIRED}
                hideIndicator
                className="pl-2 cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <CircleOff className="mt-1 h-4 w-4 text-blue-600" />
                  <div className="flex flex-col text-left">
                    <span className="text-sm font-medium">
                      {t("transactions.transaction_smb_status.not_required")}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t(
                        "transactions.status_options.not_required_description",
                      )}
                    </span>
                  </div>
                </div>
              </SelectItem>
              <SelectItem
                value={SmbAttachmentStatus.LOST}
                hideIndicator
                className="pl-2 cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <X className="mt-1 h-4 w-4 text-gray-600" />
                  <div className="flex flex-col text-left">
                    <span className="text-sm font-medium">
                      {t("transactions.transaction_smb_status.lost")}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t("transactions.status_options.lost_description")}
                    </span>
                  </div>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        )}
        {selectedRows.length === 1 &&
          Object.keys(selectedInvoicesIds).length > 0 && (
            <TableActionBarButton
              variant="outline"
              onClick={() => handleMatch()}
              disabled={loading}
            >
              <Link2 className="w-4 h-4" />
              {t("transactions.actions.match")}
            </TableActionBarButton>
          )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="h-10 w-9 p-0 justify-center">
              <EllipsisVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent sideOffset={6} className="w-56">
            <DropdownMenuItem
              className="gap-2 cursor-pointer"
              onSelect={handleDelete}
              disabled={loading}
            >
              <Trash2 className="w-4 h-4" />
              {t("delete")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TableActionBarRoot>
  );

  async function handleDelete() {
    const bankTransactionIds = selectedRows.map(
      (row) => row.original.bankTxnId,
    );

    try {
      await deleteBankTransaction({ bankTransactionIds });
      table.resetRowSelection();
    } catch (error) {
      console.error("Failed to delete transactions:", error);
    }
  }

  async function handleChangeStatus(newSmbAttachmentStatus: string) {
    const bankTransactionIds = selectedRows.map(
      (row) => (row.original as Transaction).bankTxnId,
    );

    if (!newSmbAttachmentStatus) return;

    try {
      await updateBankTransaction({
        bankTransactionIds,
        updatedFields: {
          smbAttachmentStatus: newSmbAttachmentStatus as SmbAttachmentStatus,
        },
      });
    } catch (error) {
      console.error("Failed to change attachment status:", error);
    }
  }
};
